import type { FeatureCollection } from 'geojson';

import { apiClient } from './apiClient';
import { normaliseFeatureCollectionResponse } from './geoJsonUtils';

/**
 * Fetches the entity-shaped map features within the given viewport. Uses
 * GET /pois/md/{bbox} (LeakDetectionModule policy): one POI feature per root row plus
 * one leak feature per confirmed-root/child row, each with its own geometry,
 * `entity_type`, opaque id ('leak:'-prefixed for leaks — never parsed client-side) and
 * split-model status.
 *
 * bbox format: "minLng,minLat,maxLng,maxLat" — matches BoundingBox.TryParse.
 * The bbox is URL-encoded as a path segment (commas → %2C).
 *
 * Passes clientId as a query parameter so the ExternalAPI can scope the response
 * to the selected client rather than the caller's JWT claim client (Origin users).
 */
export async function fetchEntitiesForBounds(clientId: string, bbox: string): Promise<FeatureCollection> {
    const encodedBbox = encodeURIComponent(bbox);
    const response = await apiClient.get<unknown>(`/pois/md/${encodedBbox}`, { params: { clientId } });
    return normaliseFeatureCollectionResponse(response.data);
}

/**
 * Entity-shaped worklist feed (the group endpoint) for Previous/Next navigation, which
 * steps through POI entities only — leak features are family satellites reached from the
 * POI panel, not worklist stops. The caller filters to the POI family.
 */
export async function fetchEntitiesForWorklist(clientId: string, groupId: string): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>(`/pois/md/group/${encodeURIComponent(groupId)}`, {
        params: { clientId },
    });
    return normaliseFeatureCollectionResponse(response.data);
}

/**
 * Entity-shaped features for the whole client network. Uses GET /pois/md/network
 * (LeakDetectionModule policy) — unlike the viewport endpoint it is not bbox-capped, so
 * it can back the cluster bubbles at Network Overview zoom levels. Each feature carries
 * only the summary properties the cluster source and client-side Layers/Filters panels
 * need.
 */
export async function fetchEntitiesForNetworkMap(clientId: string): Promise<FeatureCollection> {
    const response = await apiClient.get<unknown>('/pois/md/network', { params: { clientId } });
    return normaliseFeatureCollectionResponse(response.data);
}

/**
 * Shape of a single POI returned by GET /pois/network for CSV export.
 * All fields required for the CSV are present (unlike the GeoJSON viewport endpoint).
 */
export interface NetworkPoiExportItem {
    /** The platform.poi row id — the join key for the per-POI pipeline associations. */
    id: string;
    areaName: string;
    dmaIdent: string;
    dmaName: string;
    /** The worklist (poi_group) name — shown as "Worklist" wherever the UI surfaces it. */
    groupName: string;
    stop: number;
    /** Child-leak ordinal; null on root rows (roots must never be labelled "N-0"). */
    suffix: number | null;
    /**
     * Computed split-model statuses. poiEntityStatus is non-null exactly on root rows
     * (children are never POI entities); leakEntityStatus is non-null on every row that
     * projects a leak entity (all children + confirmed roots). Together they ARE the
     * entity projection — the export needs no parent linkage on the wire.
     */
    poiEntityStatus: string | null;
    leakEntityStatus: string | null;
    /** ISO "yyyy-MM-dd" */
    surveyedDate: string;
    whatThreeWords: string;
    longitude: number;
    latitude: number;
    leakWhatThreeWords: string | null;
    leakLongitude: number | null;
    leakLatitude: number | null;
    /** LeakageResults enum integer: 0=AwaitingAssessment, 1=Yes(Leak), 2=No(NoLeak), 3=Inconclusive */
    leakageResult: number;
    /** PipeTypes enum integer: 0=Comms, 1=CSPL, 2=Mains, 3=Fitting, 4=ST/BB, or null */
    pipeType: number | null;
    /** ISO "yyyy-MM-dd" or null */
    searchDate: string | null;
    fixed: boolean | null;
    /** Whether the leak was visible at the surface. null when not recorded. */
    isLeakVisible: boolean | null;
    /** Whether the POI was flagged as a no-dig repair. null when not recorded. */
    nodigFix: boolean | null;
}

/**
 * Fetches all Points of Interest for the given client, used for CSV export.
 * Uses GET /pois/network (LeakDetectionModule policy) with an optional clientId
 * override so Origin users can export data for the currently selected client.
 */
export async function fetchAllPoisForDownload(clientId: string): Promise<NetworkPoiExportItem[]> {
    const response = await apiClient.get<NetworkPoiExportItem[]>('/pois/network', {
        params: { clientId },
    });
    return response.data;
}

/**
 * Shape of a single POI returned by GET /pois/search/{Term}.
 * Contains all fields needed to populate PoiFeatureProperties and fly to coordinates.
 */
export interface SearchPoiResult {
    id: string;
    status: string;
    name: string;
    groupId: string;
    stop: number;
    area: string;
    dmaIdent: string;
    dmaName: string;
    isPlanned: boolean;
    what3Words: string;
    pipeType: string | null;
    assumedLeakageRate: number;
    measuredLeakageRate: number | null;
    leakWhat3Words: string | null;
    surveyedDate: string | null;
    searchDate: string | null;
    isLeakVisible: boolean | null;
    repaired: boolean | null;
    nodigFix?: boolean | null;
    longitude: number;
    latitude: number;
    /** 'poi' for root rows, 'leak' for child rows (a recorded leak). */
    entityType?: string;
    /** The result's map feature id under the entity contract — opaque, never parsed client-side. */
    entityId?: string;
    /** The family root's id, for history fetches and family navigation. */
    familyPoiId?: string;
    /** "21-1" for child leaks; null for root rows. */
    suffixLabel?: string | null;
    /** Split-model POI-entity status; null for child rows. */
    poiEntityStatus?: string | null;
    /** Split-model leak-entity status; null when the row projects no leak entity. */
    leakEntityStatus?: string | null;
}

/**
 * Text searches POIs for the given client by name, area, what3words, DMA name/ident,
 * or leakage address. Uses GET /pois/search/{Term} (LeakDetectionModule policy).
 * When stopNumber is provided the backend switches to an exact match on p.stop.
 * Returns at most 10 results ordered by area, DMA, name, stop.
 */
export async function searchPoisForText(
    term: string,
    clientId: string,
    stopNumber?: number,
): Promise<SearchPoiResult[]> {
    const params: Record<string, string | number> = { clientId };
    if (stopNumber !== undefined) params.stopNumber = stopNumber;
    const response = await apiClient.get<SearchPoiResult[]>(`/pois/search/${encodeURIComponent(term)}`, { params });
    return response.data;
}
